import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

const API_BASE_URL = 'https://stu129274.webhosting.arden.ac.uk/COM6008/api';

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isTokenSet, setIsTokenSet] = useState(false);

  // Set authorization header immediately when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('Token set in axios headers:', token.substring(0, 20) + '...');
      setIsTokenSet(true);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      console.log('Authorization header removed');
      setIsTokenSet(false);
    }
  }, [token]);

  // Add axios request interceptor to ensure token is always attached
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const currentToken = localStorage.getItem('token');
        if (currentToken && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${currentToken}`;
          console.log('Request interceptor: Token attached to request');
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('401 error detected, token may be invalid');
          // Don't auto-logout here as it might be a race condition
          // Let individual components handle 401 errors
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Check if user is logged in on app start
  useEffect(() => {
    const initAuth = async () => {
      if (token && isTokenSet) {
        try {
          console.log('Checking token validity...');
          const response = await axios.get('/profile');
          setUser(response.data);
          console.log('User authenticated:', response.data.username);
        } catch (error) {
          console.log('Token validation failed:', error.response?.status);
          // Token is invalid
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    if (token) {
      // Only check auth if token exists and header is set
      if (isTokenSet) {
        initAuth();
      }
    } else {
      // No token, finish loading
      setLoading(false);
    }
  }, [token, isTokenSet]);

  const login = async (username, password) => {
    try {
      const response = await axios.post('/login', {
        username,
        password
      });

      const { access_token, user } = response.data;
      
      // Set token in localStorage first
      localStorage.setItem('token', access_token);
      
      // Set token state - this will trigger the axios header setting
      setToken(access_token);
      
      // Wait a tiny bit to ensure the axios header is set
      await new Promise(resolve => setTimeout(resolve, 10));
      
      // Set user after token is properly configured
      setUser(user);
      
      console.log('Login successful for user:', user.username);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error.response?.data);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post('/register', userData);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsTokenSet(false);
    console.log('User logged out');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/profile', profileData);
      setUser(response.data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Profile update failed' 
      };
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user && !!token && isTokenSet,
    isAdmin: user?.role === 'Admin',
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 